import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../helpers/axiosInstance";
import { toast } from "react-toastify";

const initialState = {
    participantsData: [],
    pagination: null,
    loading: false,
    error: null,
    logoSizesOptions: [],
    logoFormatsOptions: [],
    authenticationOptions: [],
    otpValidityOptions: [],
    otpCharacteristicsOptions: [],
    otpDeliveryOptions: [],
    smsVendorsOptions: [],
    cardValidatorsOptions: [],
    countryList: [],
};

export const getAllParticipants = createAsyncThunk(
    "participant-management/participants",
    async ({ user, pagination, orderBy, search }) => {
        try {
            const { url, endpoint } = user?.routesConfig;
            const { pageSize, currentPage } = pagination;
            // const { sortColumn, isAsce } = orderBy;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.getallparticipants;

            // const apiURL = base + endpointpath + `${pageSize}/${currentPage}/${sortColumn}/${isAsce}`;
            const apiURL = base + endpointpath + `${pageSize}/${currentPage}`;

            const response = await axiosInstance.post(apiURL, search);
            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

export const getParticipantsLogoDetail = createAsyncThunk(
    "participant-management/participant-logo-detail",
    async (user) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.participantLogoSizeAndFormat;

            const apiURL = base + endpointpath;

            const response = await axiosInstance.get(apiURL);
            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

export const getParticipantsMessageInfo = createAsyncThunk(
    "participant-management/participant-message-info",
    async (user) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.getmessageinformation;

            const apiURL = base + endpointpath;

            const response = await axiosInstance.get(apiURL);
            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

export const getCountryList = createAsyncThunk(
    "participant-management/country-list",
    async (user) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.getallcurrency;

            const apiURL = base + endpointpath;

            const response = await axiosInstance.get(apiURL);
            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

export const postAddUpdateParticipant = createAsyncThunk(
    "participant-management/add-update-participant",
    async ({ user, body, update }) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = update ? endpoint?.acsbackofficemanagement?.updateparticipant : endpoint?.acsbackofficemanagement?.addparticipant;

            const apiURL = base + endpointpath;

            const method = update ? 'post' : 'post';
            const response = await axiosInstance[method](apiURL, body);

            if(response?.data?.responseCode === '403' || response?.data?.responseCode === '400') {
                toast.error(response?.data?.responseDescription);
                return { status: response?.data?.responseCode };
            }

            toast.success('Request has been initiated and sent to checker');
            return { ...response?.data, status: response.status };
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

const ParticipantsSlice = createSlice({
    name: "participants",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // GET ALL PARTICIPANTS
            .addCase(getAllParticipants.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllParticipants.fulfilled, (state, action) => {
                const { data, currentPage, pageSize, totalItem, totalPages } = action?.payload;
                state.loading = false;
                state.participantsData = data || [];
                state.pagination = { currentPage, pageSize, totalItem, totalPages };
            })
            .addCase(getAllParticipants.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })

            // GET PARTICIPANT LOGO DETAILS
            .addCase(getParticipantsLogoDetail.fulfilled, (state, action) => {
                const { logoSizes, logoFormats } = action?.payload.data;
                const logoSizesArr = logoSizes.map(({ value }) => ({ label: value, value }));
                const logoFormatsArr = logoFormats.map(({ value }) => ({ label: value, value: value.toLowerCase() }));
                state.logoSizesOptions = logoSizesArr;
                state.logoFormatsOptions = logoFormatsArr;
            })

            // GET PARTICIPANT MESSAGE INFO
            .addCase(getParticipantsMessageInfo.fulfilled, (state, action) => {
                const { authenticationTypes, otpValidity, otpCharacteristics, otpDelivery, smsVendors, cardValidator } = action?.payload?.data;
                
                state.authenticationOptions = authenticationTypes?.filter(e => e.isActive).map(({ description, id }) => ({ label: description, value: id }));
                state.otpValidityOptions = otpValidity?.filter(e => e.isActive).map(({ description, id }) => ({ label: description, value: id }));
                state.otpCharacteristicsOptions = otpCharacteristics?.filter(e => e.isActive).map(({ description, id }) => ({ label: description, value: id }));
                state.otpDeliveryOptions = otpDelivery?.filter(e => e.isActive).map(({ deliveryType, id }) => ({ label: deliveryType, value: id }));
                state.smsVendorsOptions = smsVendors?.filter(e => e.isActive).map(({ vendorName, id }) => ({ label: vendorName, value: id }));
                state.cardValidatorsOptions = cardValidator?.filter(e => e.isActive).map(({ validatorName, id }) => ({ label: validatorName, value: id }));
            })

            // ADD/UPDATE PARTICIPANT
            .addCase(postAddUpdateParticipant.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(postAddUpdateParticipant.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(postAddUpdateParticipant.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })

            // Get All Countries for Currency
            .addCase(getCountryList.fulfilled, (state, action) => {
                state.countryList = action?.payload?.data || [];
            });
    },
});

export default ParticipantsSlice.reducer;